import { useState } from "react";
import { toast } from "sonner";
import { Field, Card, SaveButton, GhostButton, FileLabel, inputCls, areaCls } from "../ui";
import { adminFetch, adminUpload } from "../adminApi";

export default function GalleryTab({ content, reload }) {
  const [draft, setDraft] = useState(() => JSON.parse(JSON.stringify(content.gallery || [])));
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [newItem, setNewItem] = useState({ title: "", vehicle: "", service: "", before: null, after: null });
  const [uploading, setUploading] = useState(false);
  const [replacing, setReplacing] = useState(false);

  const update = (idx, key, value) =>
    setDraft((d) => d.map((item, i) => (i === idx ? { ...item, [key]: value } : item)));
  const remove = (idx) => setDraft((d) => d.filter((_, i) => i !== idx));

  const move = (idx, dir) =>
    setDraft((d) => {
      const copy = [...d];
      const j = idx + dir;
      if (j < 0 || j >= copy.length) return d;
      [copy[idx], copy[j]] = [copy[j], copy[idx]];
      return copy.map((item, i) => ({ ...item, order: i + 1 }));
    });

  const uploadTo = async (file) => {
    const r = await adminUpload(file);
    return r.url;
  };

  const addPending = async () => {
    if (!newItem.before || !newItem.after) {
      toast.error("Bitte beide Bilder auswählen (Vorher + Nachher).");
      return;
    }
    setUploading(true);
    try {
      const beforeUrl = await uploadTo(newItem.before);
      const afterUrl = await uploadTo(newItem.after);
      setDraft((d) => [
        ...d,
        {
          id: `gal-${Date.now()}`,
          title: newItem.title,
          vehicle: newItem.vehicle,
          service: newItem.service,
          desc: "",
          date: "",
          before_url: beforeUrl,
          after_url: afterUrl,
          visible: true,
          order: d.length + 1,
        },
      ]);
      setNewItem({ title: "", vehicle: "", service: "", before: null, after: null });
      toast.success("Bilder hochgeladen – jetzt unten auf Speichern klicken.");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setUploading(false);
    }
  };

  const replaceImage = async (idx, key, file) => {
    setReplacing(true);
    try {
      const url = await uploadTo(file);
      update(idx, key, url);
      toast.success("Bild ersetzt – jetzt unten auf Speichern klicken.");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setReplacing(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await adminFetch("/admin/content/gallery", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(draft.map((item, i) => ({ ...item, order: i + 1 }))),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      await reload();
      toast.success("Galerie gespeichert – live auf der Website.");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <p className="text-sm text-zinc-400 leading-relaxed">
        Echte Vorher-/Nachher-Fälle deiner Arbeit. Die Bilder bleiben dauerhaft gespeichert und
        überleben auch ein neues Deployment. Gelöschte Bilder werden endgültig entfernt, sobald du
        speicherst. Inaktive Fälle bleiben im Admin sichtbar, verschwinden aber von der Website.
      </p>

      <Card title="Neuen Fall erstellen">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Fahrzeug / Typ (z. B. Hyundai Tucson)">
            <input data-testid="admin-gallery-new-vehicle" className={inputCls} value={newItem.vehicle} onChange={(e) => setNewItem((n) => ({ ...n, vehicle: e.target.value }))} maxLength={120} />
          </Field>
          <Field label="Durchgeführte Leistung">
            <input data-testid="admin-gallery-new-service" className={inputCls} value={newItem.service} onChange={(e) => setNewItem((n) => ({ ...n, service: e.target.value }))} maxLength={120} placeholder="z. B. Innenraumaufbereitung" />
          </Field>
        </div>
        <Field label="Titel (optional, z. B. Innenraum komplett)">
          <input data-testid="admin-gallery-new-title" className={inputCls} value={newItem.title} onChange={(e) => setNewItem((n) => ({ ...n, title: e.target.value }))} maxLength={120} />
        </Field>
        <div className="flex flex-wrap gap-3">
          <FileLabel testid="admin-gallery-before-file" onFile={(f) => setNewItem((n) => ({ ...n, before: f }))}>
            {newItem.before ? `Vorher: ${newItem.before.name}` : "Vorher-Bild wählen"}
          </FileLabel>
          <FileLabel testid="admin-gallery-after-file" onFile={(f) => setNewItem((n) => ({ ...n, after: f }))}>
            {newItem.after ? `Nachher: ${newItem.after.name}` : "Nachher-Bild wählen"}
          </FileLabel>
          <GhostButton onClick={addPending} testid="admin-gallery-add">
            {uploading ? "Wird hochgeladen …" : "+ Hinzufügen"}
          </GhostButton>
        </div>
      </Card>

      {draft.map((item, idx) => (
        <Card
          key={item.id}
          title={item.title || item.vehicle || `Fall ${idx + 1}`}
          actions={
            <div className="flex items-center gap-2">
              <button
                type="button"
                data-testid={`admin-gallery-visible-${item.id}`}
                onClick={() => update(idx, "visible", item.visible === false)}
                className={`h-9 rounded-full border px-4 text-xs font-semibold transition-colors ${
                  item.visible === false
                    ? "border-zinc-700 text-zinc-400"
                    : "border-emerald-500/40 bg-emerald-500/10 text-emerald-400"
                }`}
              >
                {item.visible === false ? "Versteckt" : "Sichtbar"}
              </button>
              <GhostButton onClick={() => move(idx, -1)} testid={`admin-gallery-up-${item.id}`}>
                ↑
              </GhostButton>
              <GhostButton onClick={() => move(idx, 1)} testid={`admin-gallery-down-${item.id}`}>
                ↓
              </GhostButton>
              <GhostButton danger onClick={() => remove(idx)} testid={`admin-gallery-delete-${item.id}`}>
                Löschen
              </GhostButton>
            </div>
          }
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Fahrzeug / Typ">
              <input data-testid={`admin-gallery-vehicle-${item.id}`} className={inputCls} value={item.vehicle || ""} onChange={(e) => update(idx, "vehicle", e.target.value)} maxLength={120} />
            </Field>
            <Field label="Durchgeführte Leistung">
              <input className={inputCls} value={item.service || ""} onChange={(e) => update(idx, "service", e.target.value)} maxLength={120} />
            </Field>
            <Field label="Titel">
              <input className={inputCls} value={item.title || ""} onChange={(e) => update(idx, "title", e.target.value)} maxLength={120} />
            </Field>
            <Field label="Datum (optional, z. B. Oktober 2026)">
              <input data-testid={`admin-gallery-date-${item.id}`} className={inputCls} value={item.date || ""} onChange={(e) => update(idx, "date", e.target.value)} maxLength={20} />
            </Field>
          </div>
          <Field label="Kurze Beschreibung">
            <textarea className={areaCls} value={item.desc || ""} onChange={(e) => update(idx, "desc", e.target.value)} maxLength={1000} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <img src={item.before_url} alt="Vorher" className="h-28 w-full rounded-lg object-cover border border-zinc-800" />
              <div className="mt-2">
                <FileLabel testid={`admin-gallery-replace-before-${item.id}`} onFile={(f) => replaceImage(idx, "before_url", f)}>
                  Vorher ersetzen
                </FileLabel>
              </div>
            </div>
            <div>
              <img src={item.after_url} alt="Nachher" className="h-28 w-full rounded-lg object-cover border border-zinc-800" />
              <div className="mt-2">
                <FileLabel testid={`admin-gallery-replace-after-${item.id}`} onFile={(f) => replaceImage(idx, "after_url", f)}>
                  Nachher ersetzen
                </FileLabel>
              </div>
            </div>
          </div>
        </Card>
      ))}

      {replacing && <p className="text-xs text-zinc-400">Bild wird hochgeladen …</p>}

      <div className="sticky bottom-4 flex items-center gap-3 rounded-2xl border border-[#27272A] bg-[#0C0C0E]/95 p-4 backdrop-blur">
        <SaveButton onClick={save} saving={saving} saved={saved} testid="admin-save-gallery" />
        <GhostButton onClick={() => setDraft(JSON.parse(JSON.stringify(content.gallery || [])))} testid="admin-reset-gallery">
          Verwerfen
        </GhostButton>
      </div>
    </div>
  );
}
